import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { BellRing, Loader2, Plus, Star, StarOff, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { OpenStatusPill } from "@/components/districts/OpenStatusPill";
import { DistrictLink } from "@/components/districts/DistrictLink";
import {
  getDistrictForManage,
  searchPlacesForDistrict,
  listStaleDistrictVenues,
  sendVenueUpdateReminders,
  setDistrictVenue,
  updateDistrict,
} from "@/lib/districts.functions";

export const Route = createFileRoute("/_authenticated/districts/$slug/manage")({
  head: () => ({
    meta: [
      { title: "District Manager | what Finland" },
      { name: "description", content: "Manage district venues, hero content and practical information." },
      { property: "og:title", content: "District Manager — what Finland" },
      { property: "og:description", content: "Manage district venues and hub content." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DistrictManage,
});

function DistrictManage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getDistrictForManage);
  const updateFn = useServerFn(updateDistrict);
  const venueFn = useServerFn(setDistrictVenue);
  const searchFn = useServerFn(searchPlacesForDistrict);

  const { data, isPending, error } = useQuery({
    queryKey: ["district-manage", slug],
    queryFn: () => getFn({ data: { slug } }),
    retry: false,
  });

  const [term, setTerm] = useState("");
  const [results, setResults] = useState<{ id: string; name: string; slug: string }[]>([]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["district-manage", slug] });
    qc.invalidateQueries({ queryKey: ["public-district", slug] });
  };

  const saveDistrict = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      updateFn({ data: { districtId: data!.district.id, ...patch } as never }),
    onSuccess: () => {
      toast.success("District updated");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const venueAction = useMutation({
    mutationFn: (vars: { placeId: string; action: "add" | "remove" | "feature" | "unfeature" }) =>
      venueFn({ data: { districtId: data!.district.id, ...vars } }),
    onSuccess: () => {
      toast.success("Venue list updated");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-navy/40" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16 text-center">
        <h1 className="font-heading text-2xl text-navy">District not available</h1>
        <p style={{ textTransform: "none" }} className="mt-2 text-sm text-navy/60">
          {error instanceof Error ? error.message : "You don't have master access to this district."}
        </p>
      </div>
    );
  }

  const { district, venues } = data;

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-6 sm:py-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl text-navy sm:text-3xl">{district.name}</h1>
          <DistrictLink
            slug={slug}
            style={{ textTransform: "none" }}
            className="mt-1 inline-block text-xs text-royal hover:underline"
          >
            View public hub →
          </DistrictLink>
        </div>
        <div className="flex items-center gap-3">
          <Label style={{ textTransform: "none" }} htmlFor="pub">Published</Label>
          <Switch
            id="pub"
            checked={district.published}
            onCheckedChange={(v) => saveDistrict.mutate({ published: v })}
          />
        </div>
      </div>

      {/* Hub content */}
      <section className="mt-8 rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
        <h2 className="font-heading text-lg text-navy">Hub content</h2>
        <HubForm district={district} onSave={(patch) => saveDistrict.mutate(patch)} saving={saveDistrict.isPending} />
      </section>

      {/* Stale content reminders */}
      <StaleVenuesPanel districtId={district.id} />

      {/* Venues */}
      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">

        <h2 className="font-heading text-lg text-navy">Venues in this district</h2>

        <div className="mt-4 space-y-2">
          {venues.length === 0 ? (
            <p style={{ textTransform: "none" }} className="text-sm text-navy/50">
              No venues linked yet.
            </p>
          ) : (
            venues.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-navy/10 px-3 py-2">
                <span className="flex-1 font-heading text-sm text-navy">{v.name}</span>
                <OpenStatusPill source={v} size="sm" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    venueAction.mutate({ placeId: v.id, action: v.featured ? "unfeature" : "feature" })
                  }
                  title={v.featured ? "Remove from featured" : "Feature"}
                >
                  {v.featured ? <Star className="size-4 fill-current" /> : <StarOff className="size-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => venueAction.mutate({ placeId: v.id, action: "remove" })}
                  title="Remove from district"
                >
                  <Trash2 className="size-4 text-red-500" />
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 border-t border-navy/10 pt-4">
          <Label style={{ textTransform: "none" }}>Add a venue</Label>
          <div className="mt-2 flex gap-2">
            <Input
              value={term}
              placeholder="Search published places…"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key !== "Enter") return;
                const r = await searchFn({ data: { districtId: data.district.id, query: term } });
                setResults(r as never);
              }}
            />
            <Button
              variant="outline"
              onClick={async () => {
                const r = await searchFn({ data: { districtId: data.district.id, query: term } });
                setResults(r as never);
              }}
            >
              Search
            </Button>
          </div>
          {results.length > 0 ? (
            <ul className="mt-3 space-y-1">
              {results.map((r) => (
                <li key={r.id} className="flex items-center gap-3 rounded-lg border border-navy/10 px-3 py-2">
                  <span className="flex-1 text-sm text-navy">{r.name}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => venueAction.mutate({ placeId: r.id, action: "add" })}
                  >
                    <Plus className="mr-1 size-3.5" /> Add
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function HubForm({
  district,
  onSave,
  saving,
}: {
  district: {
    tagline: string | null;
    description: string | null;
    hero_image_url: string | null;
    address: string | null;
    official_partner: boolean;
    practical_info: { title: string; body: string }[];
    map_points?: { kind: "entrance" | "parking"; label: string; note?: string | null; lat: number; lng: number }[];
  };
  onSave: (patch: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [tagline, setTagline] = useState(district.tagline ?? "");
  const [description, setDescription] = useState(district.description ?? "");
  const [hero, setHero] = useState(district.hero_image_url ?? "");
  const [address, setAddress] = useState(district.address ?? "");
  const [partner, setPartner] = useState(district.official_partner);
  const [info, setInfo] = useState(district.practical_info);
  const [points, setPoints] = useState<
    { kind: "entrance" | "parking"; label: string; note?: string | null; lat: number | string; lng: number | string }[]
  >(district.map_points ?? []);


  return (
    <div className="mt-4 space-y-4">
      <div>
        <Label style={{ textTransform: "none" }}>Tagline</Label>
        <Input className="mt-1.5" value={tagline} onChange={(e) => setTagline(e.target.value)} />
      </div>
      <div>
        <Label style={{ textTransform: "none" }}>Description</Label>
        <Textarea className="mt-1.5" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div>
        <Label style={{ textTransform: "none" }}>Hero image URL</Label>
        <Input className="mt-1.5" value={hero} onChange={(e) => setHero(e.target.value)} />
      </div>
      <div>
        <Label style={{ textTransform: "none" }}>Address</Label>
        <Input className="mt-1.5" value={address} onChange={(e) => setAddress(e.target.value)} />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-navy/10 px-3 py-2">
        <Label style={{ textTransform: "none" }} htmlFor="partner">Official Partner badge</Label>
        <Switch id="partner" checked={partner} onCheckedChange={setPartner} />
      </div>

      <div className="rounded-xl border border-navy/10 p-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-sm text-navy">Practical info cards</h3>
          <Button size="sm" variant="outline" onClick={() => setInfo([...info, { title: "", body: "" }])}>
            <Plus className="mr-1 size-3.5" /> Add card
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {info.map((card, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-navy/10 p-3">
              <Input
                placeholder="Title (e.g. Parking)"
                value={card.title}
                onChange={(e) =>
                  setInfo(info.map((c, idx) => (idx === i ? { ...c, title: e.target.value } : c)))
                }
              />
              <Textarea
                rows={2}
                placeholder="Details"
                value={card.body}
                onChange={(e) => setInfo(info.map((c, idx) => (idx === i ? { ...c, body: e.target.value } : c)))}
              />
              <Button size="sm" variant="ghost" onClick={() => setInfo(info.filter((_, idx) => idx !== i))}>
                <Trash2 className="mr-1 size-3.5 text-red-500" /> Remove
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 p-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-sm text-navy">Map points (entrances & parking)</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPoints([...points, { kind: "entrance", label: "", note: "", lat: "", lng: "" }])}
          >
            <Plus className="mr-1 size-3.5" /> Add point
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {points.map((p, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-navy/10 p-3">
              <div className="flex gap-2">
                <select
                  value={p.kind}
                  onChange={(e) =>
                    setPoints(points.map((q, idx) => (idx === i ? { ...q, kind: e.target.value as "entrance" | "parking" } : q)))
                  }
                  style={{ textTransform: "none" }}
                  className="rounded-md border border-navy/15 bg-white px-2 text-sm"
                >
                  <option value="entrance">Entrance</option>
                  <option value="parking">Parking</option>
                </select>
                <Input
                  placeholder="Label (e.g. Main entrance)"
                  value={p.label}
                  onChange={(e) => setPoints(points.map((q, idx) => (idx === i ? { ...q, label: e.target.value } : q)))}
                />
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Latitude"
                  value={String(p.lat)}
                  onChange={(e) => setPoints(points.map((q, idx) => (idx === i ? { ...q, lat: e.target.value } : q)))}
                />
                <Input
                  placeholder="Longitude"
                  value={String(p.lng)}
                  onChange={(e) => setPoints(points.map((q, idx) => (idx === i ? { ...q, lng: e.target.value } : q)))}
                />
              </div>
              <Input
                placeholder="Note (optional, e.g. 2h free)"
                value={p.note ?? ""}
                onChange={(e) => setPoints(points.map((q, idx) => (idx === i ? { ...q, note: e.target.value } : q)))}
              />
              <Button size="sm" variant="ghost" onClick={() => setPoints(points.filter((_, idx) => idx !== i))}>
                <Trash2 className="mr-1 size-3.5 text-red-500" /> Remove
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          disabled={saving}
          onClick={() =>
            onSave({
              tagline: tagline || null,
              description: description || null,
              hero_image_url: hero || null,
              address: address || null,
              official_partner: partner,
              practical_info: info.filter((c) => c.title.trim() || c.body.trim()),
              map_points: points
                .map((p) => ({
                  kind: p.kind,
                  label: p.label.trim() || (p.kind === "parking" ? "Parking" : "Entrance"),
                  note: (p.note ?? "").trim() || null,
                  lat: Number(p.lat),
                  lng: Number(p.lng),
                }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0)),
            })
          }
        >
          {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Save hub content
        </Button>
      </div>
    </div>
  );
}


function StaleVenuesPanel({ districtId }: { districtId: string }) {
  const listFn = useServerFn(listStaleDistrictVenues);
  const remindFn = useServerFn(sendVenueUpdateReminders);
  const [threshold, setThreshold] = useState(30);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data, isPending, refetch } = useQuery({
    queryKey: ["district-stale-venues", districtId, threshold],
    queryFn: () => listFn({ data: { districtId, thresholdDays: threshold } }),
  });

  const venues = data?.venues ?? [];
  const chosen = venues.filter((v) => selected[v.id]).map((v) => v.id);

  const remind = useMutation({
    mutationFn: (placeIds: string[]) => remindFn({ data: { districtId, placeIds } }),
    onSuccess: (res) => {
      toast.success(
        res.skipped.length > 0
          ? `Reminders sent: ${res.sent}. Skipped (no email): ${res.skipped.join(", ")}`
          : `Reminders sent: ${res.sent}`,
      );
      setSelected({});
      void refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="mt-6 rounded-2xl border border-amber-300/60 bg-amber-50/60 p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg text-navy">
            <BellRing className="size-4 text-amber-600" /> Not updated recently
          </h2>
          <p style={{ textTransform: "none" }} className="mt-1 text-xs text-navy/60">
            Venues whose hours or menus haven't been touched in {threshold}+ days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label style={{ textTransform: "none" }} htmlFor="stale-days" className="text-xs">
            Days
          </Label>
          <Input
            id="stale-days"
            type="number"
            min={1}
            max={365}
            className="h-9 w-20 bg-white"
            value={threshold}
            onChange={(e) => setThreshold(Math.min(365, Math.max(1, Number(e.target.value) || 30)))}
          />
        </div>
      </div>

      {isPending ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-navy/50">
          <Loader2 className="size-4 animate-spin" /> Checking…
        </div>
      ) : venues.length === 0 ? (
        <p style={{ textTransform: "none" }} className="mt-4 text-sm text-navy/60">
          All venues are up to date. Nothing to chase.
        </p>
      ) : (
        <>
          <ul className="mt-4 space-y-2">
            {venues.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-white px-3 py-2">
                <input
                  type="checkbox"
                  className="size-4 accent-amber-600"
                  checked={!!selected[v.id]}
                  onChange={(e) => setSelected((s) => ({ ...s, [v.id]: e.target.checked }))}
                  aria-label={`Select ${v.name}`}
                />
                <span className="flex-1 font-heading text-sm text-navy">{v.name}</span>
                <span style={{ textTransform: "none" }} className="text-xs text-navy/55">
                  {v.days_stale === null ? "never updated" : `${v.days_stale} days ago`}
                </span>
                <span style={{ textTransform: "none" }} className="text-xs text-navy/40">
                  {v.email ?? "no email on file"}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={chosen.length === 0 || remind.isPending}
              onClick={() => remind.mutate(chosen)}
            >
              {remind.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Send reminder ({chosen.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={remind.isPending || venues.length === 0}
              onClick={() => remind.mutate(venues.map((v) => v.id))}
            >
              Remind all
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
